import { localStorage } from 'dibs-browser-storage';
import { fetchQuery, graphql, Environment } from 'react-relay';

import { trackEvent } from 'dibs-tracking';

import {
    STORAGE_KEY_PDP_VISITS,
    SOURCING_MIN_PRICE,
    MEDIAN_ITEM_VALUE_INDEX,
} from './pdpSharedConsumerSourcingHelpersConstants';
import {
    evaluatePdpVisit,
    removeUserPdpVisitsInfo,
} from './pdpSharedConsumerSourcingEvaluationHelpers';
import { getUserPdpVisitsInfo } from './pdpSharedConsumerSourcingPdpInfoHelper';

import { pdpSharedConsumerSourcingModalDisplayHelpersQuery } from './__generated__/pdpSharedConsumerSourcingModalDisplayHelpersQuery.graphql';
import { PdpRespConsumerSourcingModal_item } from '../../PdpRespConsumerSourcingModal/__generated__/PdpRespConsumerSourcingModal_item.graphql';
import { PdpMobileConsumerSourcingModal_item } from '../../PdpMobileConsumerSourcingModal/__generated__/PdpMobileConsumerSourcingModal_item.graphql';

export const shouldSeeConsumerSourcingModal = async ({
    itemId,
    userId,
    environment,
}: {
    itemId: string;
    userId: string;
    environment: Environment;
}): Promise<boolean> => {
    if (!itemId) {
        return false;
    }
    try {
        const response = await fetchQuery<pdpSharedConsumerSourcingModalDisplayHelpersQuery>(
            environment,
            graphql`
                query pdpSharedConsumerSourcingModalDisplayHelpersQuery($itemId: String!) {
                    viewer {
                        item(itemId: $itemId) {
                            pricing {
                                retailPrice {
                                    convertedAmounts {
                                        USD
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            { itemId }
        );
        const itemResult = response?.viewer?.item;
        const itemPrice = itemResult?.pricing?.retailPrice?.convertedAmounts?.USD;
        if (!itemPrice || itemPrice < SOURCING_MIN_PRICE) {
            return false;
        }
        return evaluatePdpVisit({ itemId, userId, itemPrice });
    } catch (e) {
        // @ts-ignore
        if (window.newrelic) {
            // @ts-ignore
            window.newerelic.noticeError(`error fetching item price: ${e}`);
        }
    }
    return false;
};

export const suppressMessage: ({ userId }: { userId: string }) => void = ({ userId }) => {
    const updatedPdpUserSpecificVisits = removeUserPdpVisitsInfo({ userId });
    updatedPdpUserSpecificVisits.push({
        userId: userId,
        pdpInfo: [],
        suppressMessage: true,
    });
    localStorage.setItem(STORAGE_KEY_PDP_VISITS, updatedPdpUserSpecificVisits);
};

export const resetCounter: ({ userId }: { userId: string }) => void = ({ userId }) => {
    localStorage.setItem(STORAGE_KEY_PDP_VISITS, removeUserPdpVisitsInfo({ userId }));
};

export const shouldSuppressMessage: ({ userId }: { userId: string }) => boolean = ({ userId }) => {
    const { userPdpVisitsInfo, userIndex } = getUserPdpVisitsInfo({ userId });
    const userPdp = userPdpVisitsInfo.splice(userIndex, 1)[0];
    return userIndex === -1 ? false : !!userPdp?.suppressMessage;
};

export const getSubmissionInfo: ({
    userId,
}: {
    userId: string;
}) => { retailPrice: number; medianPricePoint: number } = ({ userId }) => {
    const { userPdpVisitsInfo, userIndex } = getUserPdpVisitsInfo({ userId });
    const userPdp = userPdpVisitsInfo[userIndex];
    const mostRecentPdp = userPdp.pdpInfo[userPdp.pdpInfo.length - 1];
    const sortedVisits = userPdp.pdpInfo.sort((a, b) => a.itemPrice - b.itemPrice);

    return {
        retailPrice: mostRecentPdp.itemPrice,
        medianPricePoint: sortedVisits[MEDIAN_ITEM_VALUE_INDEX].itemPrice,
    };
};

export const trackConsumerSourcingModal = ({ action }: { action: string }): void => {
    trackEvent({
        category: 'sales tools',
        label: 'consumer sourcing modal',
        action,
    });
};

export const getItemInfo = ({
    item,
}: {
    item: PdpRespConsumerSourcingModal_item | PdpMobileConsumerSourcingModal_item;
}): { itemId: string; itemReferenceNumber: string; itemTitle: string } => {
    const itemId = item?.serviceId || '';
    const itemReferenceNumber = item?.dibsReference || '';
    const itemTitle = item?.title || '';

    return { itemId, itemReferenceNumber, itemTitle };
};
